/* eslint-disable no-restricted-imports -- type-only import of ky's hook & instance types */
import type { AfterResponseHook, KyInstance } from 'ky';

import { refreshAuthToken } from '~/api/refresh-token';
import { useAuthStore } from '~/store/auth.store';

/** Custom header marker added to the retried request so the same hook can
 *  short-circuit and avoid a refresh→retry loop if the retry still 401/403s. */
const RETRY_HEADER = 'X-Auth-Retry';

/**
 * Factory that builds a Ky `afterResponse` hook bound to a specific client.
 * Pass a lazy `getClient` so the same hook can reference the client it lives
 * inside without a circular import — the closure resolves at request time,
 * by which point the client is fully initialised.
 *
 * 401 / 403 behavior:
 *  - Bail out for unauthenticated bootstrap endpoints (login / refresh /
 *    exchange-code) so callers can show inline credential errors.
 *  - Bail out for already-retried requests (RETRY_HEADER present) so a repeat
 *    401/403 on the retried call doesn't recurse into a refresh loop.
 *  - Otherwise attempt a single token refresh, then retry the original
 *    request through the same client so every `beforeRequest` hook
 *    (bearer / subsidiary / locale / X-API-Key) is re-applied.
 *
 * Both 401 and 403 trigger refresh+retry because the platform gateway returns
 * 403 (not 401) on an expired access token (AGENT-259). A *genuine*
 * authorization 403 still surfaces: the refreshed retry 403s again, the
 * RETRY_HEADER guard returns it verbatim, and the error interceptor reports it
 * as FORBIDDEN — just one refresh+retry round-trip later than before.
 */
export function createAuthInterceptor(getClient: () => KyInstance): AfterResponseHook {
  return async (request, _options, response) => {
    if (response.status !== 401 && response.status !== 403) return response;

    // Login / refresh / exchange-code each treat 401/403 as a domain error
    // (wrong creds / bad code / invalid refresh token) rather than an
    // expired-session signal.
    if (
      request.url.includes('auth/login') ||
      request.url.includes('auth/refresh') ||
      request.url.includes('auth/exchange-code')
    ) {
      return response;
    }

    // Already retried once — don't loop. Returns the latest 401/403 verbatim so
    // TanStack Query / error boundaries can surface it to the user.
    if (request.headers.get(RETRY_HEADER)) return response;

    const outcome = await refreshAuthToken();

    if (outcome === 'refreshed') {
      const retryHeaders = new Headers(request.headers);
      retryHeaders.set(RETRY_HEADER, '1');
      // Bearer is re-attached by the client's beforeRequest chain, but we set
      // it explicitly too so the retry is correct even if a beforeRequest
      // hook is ever removed.
      const { accessToken } = useAuthStore.getState();
      if (accessToken) retryHeaders.set('Authorization', `Bearer ${accessToken}`);
      return getClient()(request, { headers: retryHeaders });
    }

    if (outcome === 'transient') {
      // Backend hiccup — keep the session alive and let the caller deal with
      // the 401 (TanStack Query will surface it; the user can retry once the
      // backend recovers).
      return response;
    }

    // 'invalid' — refresh token rejected. Force a full reload to /login so
    // every in-memory cache resets cleanly.
    useAuthStore.getState().clearUser();
    window.location.replace('/login');
    return response;
  };
}
